import { UserRole } from '@/components/authnz/types/rbac';
import { createPlaceholderId } from '@/lib/shared-user-roles';

/**
 * Last-admin and self-action guards (AUD-14). Pure decisions so routes and
 * tests share one implementation; the route supplies the live role map.
 */

export const LAST_ADMINISTRATOR_ERROR = 'last-administrator';
export const SELF_ACTION_UNCONFIRMED_ERROR = 'self-action-unconfirmed';
export const LAST_ADMINISTRATOR_MESSAGE = 'Cannot remove the last administrator.';
export const LAST_ADMINISTRATOR_BAN_MESSAGE = 'Cannot ban the last administrator.';
export const LAST_ADMINISTRATOR_DELETE_MESSAGE = 'Cannot delete the last administrator.';

export type LockoutRefusal = {
  allow: false;
  code: typeof LAST_ADMINISTRATOR_ERROR | typeof SELF_ACTION_UNCONFIRMED_ERROR;
  statusCode: 409;
  message: string;
};

export type LockoutDecision = { allow: true } | LockoutRefusal;

export function typedEmailMatches(typed: string, expected: string): boolean {
  return typed.trim().toLowerCase() === expected.trim().toLowerCase();
}

export function isSelfTarget(params: {
  actorUid?: string | null;
  actorEmail?: string | null;
  targetUid?: string | null;
  targetEmail?: string | null;
}): boolean {
  const actorUid = params.actorUid?.trim();
  const targetUid = params.targetUid?.trim();
  if (actorUid && targetUid && actorUid === targetUid) return true;
  const actorEmail = params.actorEmail?.toLowerCase().trim();
  const targetEmail = params.targetEmail?.toLowerCase().trim();
  return Boolean(actorEmail && targetEmail && actorEmail === targetEmail);
}

export function identitiesHoldingRole(map: Map<string, UserRole[]>, role: UserRole): string[] {
  const ids: string[] = [];
  for (const [id, roles] of map) {
    if (roles.includes(role)) ids.push(id);
  }
  return ids;
}

export function identityKeysForUser(userId?: string | null, email?: string | null): string[] {
  const keys: string[] = [];
  if (userId) keys.push(userId);
  if (email) keys.push(createPlaceholderId(email));
  return keys;
}

function remainingHoldingRole(
  map: Map<string, UserRole[]>,
  role: UserRole,
  removingIds: readonly string[],
): number {
  const removing = new Set(removingIds);
  let remaining = 0;
  for (const [id, roles] of map) {
    if (removing.has(id)) continue;
    if (roles.includes(role)) remaining += 1;
  }
  return remaining;
}

export function decideRoleRemoval(params: {
  role: UserRole;
  actorUid?: string | null;
  actorEmail?: string | null;
  targetUid?: string | null;
  targetEmail?: string | null;
  confirmSelf?: boolean;
  roleMap: Map<string, UserRole[]>;
}): LockoutDecision {
  const removingIds = identityKeysForUser(params.targetUid, params.targetEmail);
  if (params.role === UserRole.ADMINISTRATOR) {
    const remaining = remainingHoldingRole(params.roleMap, UserRole.ADMINISTRATOR, removingIds);
    if (remaining < 1) {
      return {
        allow: false,
        code: LAST_ADMINISTRATOR_ERROR,
        statusCode: 409,
        message: LAST_ADMINISTRATOR_MESSAGE,
      };
    }
  }

  const self = isSelfTarget({
    actorUid: params.actorUid,
    actorEmail: params.actorEmail,
    targetUid: params.targetUid,
    targetEmail: params.targetEmail,
  });
  if (self && params.role === UserRole.ADMINISTRATOR && !params.confirmSelf) {
    return {
      allow: false,
      code: SELF_ACTION_UNCONFIRMED_ERROR,
      statusCode: 409,
      message: 'Confirm removing your own administrator role by sending confirmSelf: true.',
    };
  }

  return { allow: true };
}

export function decideLifecycleAction(params: {
  action: 'ban' | 'delete';
  actorUid?: string | null;
  actorEmail?: string | null;
  targetUids: readonly string[];
  confirmSelf?: boolean;
  roleMap: Map<string, UserRole[]>;
}): LockoutDecision {
  const remaining = remainingHoldingRole(params.roleMap, UserRole.ADMINISTRATOR, params.targetUids);
  const targetingAnAdmin = params.targetUids.some((id) =>
    (params.roleMap.get(id) ?? []).includes(UserRole.ADMINISTRATOR),
  );
  if (targetingAnAdmin && remaining < 1) {
    return {
      allow: false,
      code: LAST_ADMINISTRATOR_ERROR,
      statusCode: 409,
      message:
        params.action === 'ban' ? LAST_ADMINISTRATOR_BAN_MESSAGE : LAST_ADMINISTRATOR_DELETE_MESSAGE,
    };
  }

  const self = params.targetUids.some((id) =>
    isSelfTarget({ actorUid: params.actorUid, actorEmail: params.actorEmail, targetUid: id }),
  );
  if (self && !params.confirmSelf) {
    return {
      allow: false,
      code: SELF_ACTION_UNCONFIRMED_ERROR,
      statusCode: 409,
      message: `Confirm ${params.action === 'ban' ? 'banning' : 'deleting'} your own account by sending confirmSelf: true.`,
    };
  }

  return { allow: true };
}
